import { useState } from 'react';

import { cn } from '@/lib/utils';
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaLoadingIndicator,
  MediaMuteButton,
  MediaPipButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from 'media-chrome/react';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  className?: string;
  onTimeUpdate?: (currentTime: number) => void;
  onEnded?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  autoPlay?: boolean;
  onRetry?: () => Promise<unknown>;
  /**
   * Take the player's shape from the MEDIA rather than assuming 16:9.
   *
   * The default is `aspect-video`, which is right for the ad previews this was
   * written for and wrong for everything the organic templates produce — those
   * are portrait, and a 9:16 video in a 16:9 box is a thin strip between two
   * black margins that take up most of the player.
   *
   * Measured from `videoWidth`/`videoHeight` on `loadedmetadata` rather than
   * read from `draftConfig.orientation`: the file is the authority on its own
   * dimensions, and a config that disagrees with the render would put the
   * letterbox back for reasons nobody could see.
   */
  fitToMedia?: boolean;
}

export function VideoPlayer({
  src,
  poster,
  className,
  onTimeUpdate,
  onEnded,
  onPlay,
  onPause,
  autoPlay = false,
  fitToMedia = false,
  onRetry,
}: VideoPlayerProps) {
  // Null until the browser has read the header. Until then the player keeps its
  // default shape rather than guessing, so it does not visibly re-proportion
  // itself a beat after mounting.
  const [ratio, setRatio] = useState<number | null>(null);
  const [paused, setPaused] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [attempt, setAttempt] = useState(0);

  async function retry() {
    setRetrying(true);
    try {
      await onRetry?.();
      setFailed(false);
      setPaused(true);
      setAttempt((current) => current + 1);
    } catch {
      setFailed(true);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <MediaController
      autohide={paused || failed ? '-1' : '2'}
      style={fitToMedia && ratio ? { aspectRatio: String(ratio) } : undefined}
      className={cn(
        'relative w-full bg-black rounded-lg overflow-hidden',
        // Only the fallback. An inline `aspect-ratio` wins over the class, and
        // `fitToMedia` without a measurement yet still needs a shape to hold.
        !(fitToMedia && ratio) && 'aspect-video',
        '[&_media-control-bar]:bg-gradient-to-t [&_media-control-bar]:from-black/80 [&_media-control-bar]:to-transparent',
        '[&_media-control-bar]:px-2 [&_media-control-bar]:py-1',
        className
      )}
    >
      <video
        key={`${src}-${attempt}`}
        slot="media"
        src={src}
        poster={poster}
        preload="metadata"
        autoPlay={autoPlay}
        onTimeUpdate={(e) => onTimeUpdate?.(e.currentTarget.currentTime)}
        onEnded={() => {
          setPaused(true);
          onEnded?.();
        }}
        onPlay={() => {
          setPaused(false);
          onPlay?.();
        }}
        onPause={() => {
          setPaused(true);
          onPause?.();
        }}
        onLoadStart={() => setFailed(false)}
        onError={() => setFailed(true)}
        onLoadedMetadata={(e) => {
          if (!fitToMedia) return;
          const { videoWidth, videoHeight } = e.currentTarget;
          if (videoWidth > 0 && videoHeight > 0) {
            setRatio(videoWidth / videoHeight);
          }
        }}
        className="w-full h-full object-contain"
      />

      {failed ? (
        <div
          slot="centered-chrome"
          role="alert"
          className="flex flex-col items-center gap-3 rounded-lg bg-black/80 p-5 text-center text-sm text-white"
        >
          <p>The video could not be loaded.</p>
          <button
            type="button"
            disabled={retrying}
            onClick={() => void retry()}
            className="rounded-md bg-white px-4 py-2 font-medium text-black"
          >
            {retrying ? 'Loading…' : 'Try again'}
          </button>
        </div>
      ) : paused ? (
        <MediaPlayButton
          slot="centered-chrome"
          aria-label="Play video"
          className="rounded-full bg-black/60 p-4"
        />
      ) : (
        <MediaLoadingIndicator slot="centered-chrome" />
      )}

      <MediaControlBar>
        <MediaPlayButton />
        <MediaSeekBackwardButton seekOffset={10} />
        <MediaSeekForwardButton seekOffset={10} />
        <MediaTimeRange />
        <MediaTimeDisplay showDuration />
        <MediaMuteButton />
        <MediaVolumeRange />
        <MediaPipButton />
        <MediaFullscreenButton />
      </MediaControlBar>
    </MediaController>
  );
}
